import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag
} from 'antd'
import PageLoader from '../../components/Layouts/PageLoader'
import PrivateRoute from '../../hocs/PrivateRoute'
import {
  getRequest,
  postRequest,
  insuranceSettlementSummaryUrl,
  insuranceLedgerUrl,
  insuranceInvoicesUrl,
  insuranceRemittancesUrl,
  insuranceBatchActionUrl,
  insuranceBrokersUrl,
  insurancePoliciesUrl,
  successNotification,
  errorNotification
} from '../../config'

const { Option } = Select
const { TabPane } = Tabs

const serverSaid = response =>
  response.error?.response?.data?.detail || response.data?.detail

const BATCH_COLOURS = {
  draft: 'default',
  sent: 'gold',
  part_paid: 'orange',
  paid: 'green',
  void: 'red'
}

const BATCH_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  part_paid: 'Partly paid',
  paid: 'Paid',
  void: 'Void'
}

const SettlementPage = () => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [remittances, setRemittances] = useState([])
  const [ledger, setLedger] = useState([])
  const [brokers, setBrokers] = useState([])
  const [open, setOpen] = useState(null) // {kind, batch}
  const [raising, setRaising] = useState(null) // 'invoices' | 'remittances'
  const [eligible, setEligible] = useState([])
  const [chosen, setChosen] = useState([])
  const [broker, setBroker] = useState(undefined)
  const [refusal, setRefusal] = useState(null)
  const [payFor, setPayFor] = useState(null)
  const [payment, setPayment] = useState({})
  const [saving, setSaving] = useState(false)

  const load = async () => {
    setLoading(true)
    const s = await getRequest(insuranceSettlementSummaryUrl, 'summary', () => {})
    if (s.success) setSummary(s.data)
    for (const [url, setter] of [
      [`${insuranceInvoicesUrl}/`, setInvoices],
      [`${insuranceRemittancesUrl}/`, setRemittances],
      [`${insuranceLedgerUrl}/`, setLedger],
      [`${insuranceBrokersUrl}/`, setBrokers]
    ]) {
      const response = await getRequest(url, 'settlement', () => {})
      if (response.success) setter(response.data.results || response.data)
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [])

  // Which policies may go on a batch. Receivables come from underwritten policies; payables from
  // paid ones. The server refuses anything else by name, so this list is a convenience rather
  // than the guard.
  const openRaise = async kind => {
    setRaising(kind)
    setChosen([])
    setRefusal(null)
    const status = kind === 'invoices' ? 'underwritten,delivered' : 'paid,submitted,underwritten,delivered'
    const response = await getRequest(
      `${insurancePoliciesUrl}/?status=${status}`,
      'policies',
      () => {}
    )
    if (response.success) setEligible(response.data.results || response.data)
  }

  const raise = async () => {
    setSaving(true)
    setRefusal(null)
    const url = raising === 'invoices' ? insuranceInvoicesUrl : insuranceRemittancesUrl
    const response = await postRequest(
      `${url}/`,
      { broker, policies: chosen },
      'batch',
      () => {}
    )
    setSaving(false)
    if (!response.success) {
      // The server refuses the WHOLE batch and names every policy that could not go on. Shown
      // above the selection with nothing lost, because an invoice quietly missing three policies
      // is one somebody sends and then has to explain.
      setRefusal(serverSaid(response) || 'That could not be raised.')
      return
    }
    successNotification(`${response.data.number} raised.`)
    setRaising(null)
    await load()
  }

  const act = async (kind, id, action, payload) => {
    setSaving(true)
    const response = await postRequest(
      insuranceBatchActionUrl(kind, id, action),
      payload || {},
      action,
      () => {}
    )
    setSaving(false)
    if (!response.success) {
      errorNotification(serverSaid(response) || 'That did not work.')
      return
    }
    successNotification('Done.')
    setPayFor(null)
    setPayment({})
    setOpen(null)
    await load()
  }

  /*
   * antd `render` functions return JSX, which the display-name rule reads as an anonymous
   * component. They are cell renderers — disabled the same way the MSRP whitelist and the
   * car-request table already do.
   */
  /* eslint-disable react/display-name */
  const batchColumns = kind => [
    { title: 'Number', dataIndex: 'number' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, r) => (
        <Tag color={BATCH_COLOURS[r.status]}>{BATCH_LABELS[r.status] || r.status}</Tag>
      )
    },
    {
      title: 'Total',
      key: 'total',
      render: (_, r) => `${r.currency} ${r.totalAmount}`
    },
    { title: 'Paid', key: 'paid', render: (_, r) => `${r.currency} ${r.paidAmount}` },
    {
      title: 'Outstanding',
      key: 'outstanding',
      render: (_, r) => `${r.currency} ${r.outstandingAmount}`
    },
    { title: 'Issued', dataIndex: 'issuedOn', render: (_, r) => r.issuedOn || '—' },
    {
      title: '',
      key: 'open',
      render: (_, r) => (
        <Button size="small" onClick={() => setOpen({ kind, batch: r })}>
          Open
        </Button>
      )
    }
  ]

  const ledgerColumns = [
    { title: 'Policy', dataIndex: 'policyLabel' },
    {
      title: 'Direction',
      dataIndex: 'direction',
      render: (_, r) =>
        r.direction === 'receivable' ? (
          <Tag color="blue">They owe us</Tag>
        ) : (
          <Tag color="purple">We owe them</Tag>
        )
    },
    { title: 'Amount', key: 'amount', render: (_, r) => `${r.currency} ${r.amount}` },
    { title: 'State', dataIndex: 'status' },
    {
      title: 'Earned',
      dataIndex: 'earnedAt',
      render: (_, r) => (r.earnedAt ? r.earnedAt.slice(0, 10) : '—')
    },
    {
      title: 'Settled',
      dataIndex: 'settledAt',
      render: (_, r) => (r.settledAt ? r.settledAt.slice(0, 10) : '—')
    }
  ]

  /* eslint-enable react/display-name */

  const tiles = summary
    ? [
        ['They owe us', summary.outstandingReceivable],
        ['We owe the broker', summary.outstandingPayable],
        ['Invoiced, unpaid', summary.invoicedUnpaid],
        ['Commission earned', summary.commissionEarned]
      ]
    : []

  return (
    <PageLoader loading={loading}>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {tiles.map(([label, value]) => (
          <Col key={label} xs={12} md={6}>
            <Card size="small">
              <div style={{ opacity: 0.65, fontSize: 12 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 600 }}>
                {summary.currency} {value}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Tabs
        defaultActiveKey="invoices"
        tabBarExtraContent={
          <Space>
            <Button type="primary" onClick={() => openRaise('invoices')}>
              Raise an invoice
            </Button>
            <Button onClick={() => openRaise('remittances')}>Raise a remittance</Button>
          </Space>
        }
      >
        <TabPane tab="Invoices (commission they owe us)" key="invoices">
          <Table
            rowKey="id"
            dataSource={invoices}
            columns={batchColumns('invoices')}
            locale={{ emptyText: <Empty description="Nothing invoiced yet" /> }}
          />
        </TabPane>
        <TabPane tab="Remittances (premiums we owe them)" key="remittances">
          <Table
            rowKey="id"
            dataSource={remittances}
            columns={batchColumns('remittances')}
            locale={{ emptyText: <Empty description="Nothing remitted yet" /> }}
          />
        </TabPane>
        <TabPane tab="Ledger" key="ledger">
          {/* The "which policy" answer for every figure in the tiles above. */}
          <Table rowKey="id" dataSource={ledger} columns={ledgerColumns} />
        </TabPane>
      </Tabs>

      <Drawer
        width={720}
        visible={!!open}
        onClose={() => setOpen(null)}
        title={open ? open.batch.number : ''}
      >
        {open && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space wrap>
              <Tag color={BATCH_COLOURS[open.batch.status]}>
                {BATCH_LABELS[open.batch.status]}
              </Tag>
              {open.batch.documentUrl && (
                <a href={open.batch.documentUrl} target="_blank" rel="noopener noreferrer">
                  Open the PDF
                </a>
              )}
            </Space>

            {open.batch.status === 'sent' || open.batch.status === 'part_paid' ? (
              <Alert
                type="info"
                showIcon
                message="Sent, so it can no longer be edited"
                description="It is a financial document in someone else's hands. To change it, void it and raise a new one."
              />
            ) : null}

            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={open.batch.lines || []}
              columns={[
                {
                  title: 'Policy',
                  dataIndex: 'policyNumber',
                  render: (_, r) => r.policyNumber || '—'
                },
                { title: 'Insurer', dataIndex: 'insurerName' },
                { title: 'Customer', dataIndex: 'customerLabel' },
                {
                  title: 'Premium',
                  dataIndex: 'premiumAmount',
                  render: (_, r) => `${open.batch.currency} ${r.premiumAmount}`
                },
                {
                  title: open.kind === 'invoices' ? 'Commission' : 'To remit',
                  dataIndex: 'amount',
                  render: (_, r) => `${open.batch.currency} ${r.amount}`
                }
              ]}
            />
            <p style={{ opacity: 0.65 }}>
              These figures are as they were when the batch was raised. Later rate changes do not
              touch them.
            </p>

            <Space wrap>
              {open.batch.status === 'draft' && (
                <Button
                  type="primary"
                  loading={saving}
                  onClick={() => act(open.kind, open.batch.id, 'send')}
                >
                  {open.kind === 'invoices' ? 'Produce the PDF and send' : 'Mark sent'}
                </Button>
              )}
              {['sent', 'part_paid'].includes(open.batch.status) && (
                <Button onClick={() => setPayFor(open)}>Record a payment</Button>
              )}
              {open.batch.status !== 'paid' && open.batch.status !== 'void' && (
                <Button
                  danger
                  onClick={() =>
                    act(open.kind, open.batch.id, 'void', { reason: 'voided from the dashboard' })
                  }
                >
                  Void
                </Button>
              )}
            </Space>
          </Space>
        )}
      </Drawer>

      <Modal
        visible={!!raising}
        width={760}
        title={raising === 'invoices' ? 'Raise a commission invoice' : 'Raise a remittance'}
        okText="Raise"
        confirmLoading={saving}
        onCancel={() => setRaising(null)}
        onOk={raise}
        okButtonProps={{ disabled: !broker || chosen.length === 0 }}
      >
        {refusal && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="Nothing was raised"
            description={refusal}
          />
        )}
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <label>Broker</label>
            <Select style={{ width: '100%' }} value={broker} onChange={setBroker}>
              {brokers.map(b => (
                <Option key={b.id} value={b.id}>
                  {b.name}
                </Option>
              ))}
            </Select>
          </div>
          <Table
            rowKey="id"
            size="small"
            pagination={{ pageSize: 8 }}
            dataSource={eligible}
            rowSelection={{
              selectedRowKeys: chosen,
              onChange: keys => setChosen(keys)
            }}
            columns={[
              {
                title: 'Policy',
                dataIndex: 'policyNumber',
                render: (_, r) => r.policyNumber || '—'
              },
              { title: 'Insurer', dataIndex: 'insurerName' },
              { title: 'Customer', dataIndex: 'ownerName' },
              {
                title: raising === 'invoices' ? 'Commission' : 'Premium',
                key: 'amount',
                render: (_, r) =>
                  raising === 'invoices' ? r.commissionAmount : r.premiumAmount
              }
            ]}
          />
        </Space>
      </Modal>

      <Modal
        visible={!!payFor}
        title="Record a payment"
        okText="Record"
        confirmLoading={saving}
        onCancel={() => setPayFor(null)}
        onOk={() =>
          act(payFor.kind, payFor.batch.id, 'payment', {
            amount: payment.amount,
            paid_on: payment.paidOn,
            method: payment.method || '',
            reference: payment.reference || ''
          })
        }
        okButtonProps={{ disabled: !payment.amount || !payment.paidOn }}
      >
        <p style={{ opacity: 0.7 }}>
          Part payments are fine — the status follows the total received, so record what actually
          arrived.
        </p>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <label>Amount</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={payment.amount}
              onChange={value => setPayment({ ...payment, amount: value })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label style={{ display: 'block' }}>Date received</label>
            <DatePicker
              style={{ width: '100%' }}
              onChange={(_, text) => setPayment({ ...payment, paidOn: text })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>How</label>
            <Input
              value={payment.method}
              onChange={e => setPayment({ ...payment, method: e.target.value })}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Their reference</label>
            <Input
              value={payment.reference}
              onChange={e => setPayment({ ...payment, reference: e.target.value })}
            />
          </Col>
        </Row>
      </Modal>
    </PageLoader>
  )
}

const SettlementRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={SettlementPage} />
  </RouterSwitch>
)

SettlementRoutes.propTypes = { match: PropTypes.object }

export default withRouter(SettlementRoutes)
